'use client'

import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import type { PiePayload, DonutPayload } from '../svs-types'
import { resolveColor } from '../color-mapping'

interface PieRendererProps {
  payload: PiePayload | DonutPayload
}

export function PieRenderer({ payload }: PieRendererProps) {
  const isDonut = payload.type === 'donut'
  const options = payload.options || {}
  const items = [...payload.data.items]

  if (options.sort === 'ascending') {
    items.sort((a, b) => a.value - b.value)
  } else if (options.sort === 'descending') {
    items.sort((a, b) => b.value - a.value)
  }

  const total = items.reduce((sum, item) => sum + item.value, 0)

  const data = items.map((item, i) => ({
    name: item.label,
    value: item.value,
    fill: resolveColor(item.color, i),
    percent: total > 0 ? ((item.value / total) * 100).toFixed(1) : '0',
  }))

  const donutOptions = isDonut ? (payload as DonutPayload).options : undefined

  const renderLabel = options.showLabels !== false
    ? (entry: { name: string; percent: string }) => {
        if (options.showPercentages) return `${entry.name} (${entry.percent}%)`
        return entry.name
      }
    : undefined

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={isDonut ? '50%' : 0}
          outerRadius="80%"
          dataKey="value"
          label={renderLabel}
          labelLine={options.showLabels !== false}
        >
          {data.map((entry, i) => (
            <Cell key={i} fill={entry.fill} />
          ))}
        </Pie>
        <Tooltip
          formatter={(value: number) => [
            options.showPercentages
              ? `${value.toLocaleString()} (${total > 0 ? ((value / total) * 100).toFixed(1) : 0}%)`
              : value.toLocaleString(),
            '',
          ]}
        />
        {options.showLegend !== false && <Legend />}
        {/* Donut center label */}
        {isDonut && donutOptions?.centerLabel && (
          <text x="50%" y="48%" textAnchor="middle" dominantBaseline="central" className="fill-foreground text-xl font-bold">
            {donutOptions.centerLabel}
          </text>
        )}
        {isDonut && donutOptions?.centerSubLabel && (
          <text x="50%" y="57%" textAnchor="middle" dominantBaseline="central" className="fill-muted-foreground text-xs">
            {donutOptions.centerSubLabel}
          </text>
        )}
      </PieChart>
    </ResponsiveContainer>
  )
}
